import {useDispatch, useSelector} from "react-redux";
import {UserActionTypes, UserState} from "./types";
import {api} from "../Api/api";
import UserTokenContext from "./UserTokenContext";
import {ContactApi, UpdateContactApi, VoucherApi} from "../Api/types";
import handleException from "../Api/handleException";
import {NotFoundException} from "../Api/exceptions";
import {addFlash} from "../Notifications/actions";
import {updateSessionContact} from "../Session/actions";
import {useCart} from "../Cart/cartService";
import {selectCart} from "../Cart/reducer";


interface AuthService {
    logout: () => Promise<void>;
    loginToken: (token: string) => Promise<UserState | undefined>;
    loginAuthorizationCode: (code: string) => Promise<UserState | undefined>;
    login: (email: string, password: string) => Promise<UserState>;
    deleteUser: (password: string) => Promise<void>;
    updateUser: (data: UpdateContactApi) => Promise<ContactApi>;
    register: (email: string, name: string, phone: string, password: string, confirmPassword: string, acceptRules: boolean, acceptMarketingRules: boolean, cardCode?: string, customFields?: Record<string, string | undefined>) => Promise<UserState>;
    refresh: () => Promise<UserState | undefined>;
    activation: (email?: string, token?: string) => Promise<UserState>;
    qrcode: () => Promise<string | undefined>;
    syncVouchers: () => Promise<VoucherApi[]>;
    buyVoucher: (productId: string) => Promise<VoucherApi | undefined>;
    changePassword: (email?: string, code?: string, password?: string, passwordConfirmation?: string) => Promise<void>
    sendActivation: (email?: string) => Promise<void>
    tempCode: () => Promise<ContactApi | undefined>
}

const parseToUserState = (contact : ContactApi) => {
    return {
        id: contact.id,
        name: contact.name,
        email: contact.email,
        phone_number: contact.phone_number,
        verified: contact.verified,
        points: contact.points,
        promotional_credits: contact.available_promotional_credit_price ? contact.available_promotional_credit_price.amount : undefined,
        group_name: contact.group_name,
        temp_card_code: contact.temp_card_code,
        temp_card_expiry_date: contact.temp_card_expiry_date,
    } as UserState
}
export const useAuth = (): AuthService => {
    const dispatch = useDispatch();
    const cartService = useCart("auth");
    const cartState = useSelector(selectCart);

    const logout = async () => {
        dispatch({
            type: UserActionTypes.LOGOUT,
            payload: undefined,
        });
        UserTokenContext.setToken(undefined);
        if (cartState && cartState.id) {
            if (cartState.contact?.reference_id) {
                await cartService.deleteUser();
            }
        }
    };
    const loginToken = async (token: string) => {
        if (!token) return;
        let data = await api.getMe(token);
        if (data === undefined) {
            dispatch(addFlash('error', "NOT_FOUND"));
            throw new Error();
        }

        return await refreshUserState(data, token);
    }

    const loginAuthorizationCode = async (code: string) => {
        if (!code) return;
        let data = await api.loginAuthorizationCode(code);
        if (data === undefined) {
            dispatch(addFlash('error', "NOT_FOUND"));
            throw new Error();
        }
        return await refreshUserState(data.contact, data.token);
    }
    const login = async (email: string, password: string) => {
        let data = await api.login(email, password, "GOCRM");
        if (data === undefined) {
            dispatch(addFlash('error', "NOT_FOUND"));
            throw new Error();
        }

        return await refreshUserState(data.contact, data.token);
    };

    const register = async (email: string, name: string, phone: string, password: string, confirmPassword: string, acceptRules: boolean, acceptMarketingRules: boolean, cardCode?: string, customFields?: Record<string, string | undefined>) => {
        let data = await api.register(email, name, phone, password, confirmPassword, acceptRules, acceptMarketingRules, cardCode, customFields);
        if (data === undefined) {
            dispatch(addFlash('error', "NOT_FOUND"));
            throw new Error();
        }

        return await refreshUserState(data.contact, data.token);
    };

    const refresh = async () => {
        let token = UserTokenContext.getToken();
        if (!token) return;
        try {
            let data = await api.getMe(token);
            if (data === undefined) {
                dispatch(addFlash('error', "NOT_FOUND"));
                throw new Error();
            }
            return await refreshUserState(data, token)
        } catch (err) {
            if (err instanceof NotFoundException) {
                await logout();
                return undefined;
            }
            const exceptionErrors = handleException(err);
            // if(exceptionErrors && exceptionErrors[0] && (exceptionErrors[0].code === "resource_not_found" || exceptionErrors[0].code === "not_found")){
            if (exceptionErrors && exceptionErrors[0] && (exceptionErrors[0].code === "resource_not_found")) {
                await logout();
                // throw err;
                return undefined;
            }
            throw err;
        }

    };
    const refreshUserState = async (contact: ContactApi, token: string) => {
        let userState = parseToUserState(contact);
        userState.token = token;
        dispatch({
            type: UserActionTypes.LOGIN,
            payload: userState,
        });
        UserTokenContext.setToken(userState.token, contact.id);
        dispatch(updateSessionContact(userState));
        if (cartState && cartState.id) {
            if (cartState.contact?.reference_id !== userState.id) {
                await cartService.refreshUser();
            }
        }
        if (userState.token) {
            try {
                await syncVouchers();
            } catch (err) {
                console.log(err);
            }
        }
        return userState;
    }
    const syncVouchers = async () => {
        const token = UserTokenContext.getToken();
        let vouchers = await api.getVouchers(token);
        dispatch({
            type: UserActionTypes.VOUCHERS,
            vouchers: vouchers,
        })
        return vouchers;
    }

    const deleteUser = async (password: string) => {
        const token = UserTokenContext.getToken();
        await api.deleteUser(password, token);
        await logout();
    };

    const updateUser = async (data: UpdateContactApi) => {
        const token = UserTokenContext.getToken();
        let response = await api.updateUser(data, token);
        if (response === undefined) {
            throw new Error();
        }
        let userState = parseToUserState(response);
        dispatch({
            type: UserActionTypes.UPDATE,
            payload: userState,
        });
        dispatch(updateSessionContact(userState));
        return response;
    };

    const activation = async (email?: string, code?: string) => {
        // let response = await axios.post(url, loginData, {headers: ApiService.getHeaders()});
        let data = await api.registerActivation(email, code);
        if (data === undefined) {
            throw new Error();
        }

        return await refreshUserState(data.contact, data.token);
    }
    const qrcode = async () => {
        let token = UserTokenContext.getToken();
        if (!token) return undefined;
        let data = await api.getQrCode(token);
        if (data === undefined) {
            return undefined;
        }
        dispatch({
            type: UserActionTypes.UPDATE_QRCODE,
            qrcode: data,
        });
        return data;
    }
    const tempCode = async () => {
        let token = UserTokenContext.getToken();
        if (!token) return undefined;
        let data = await api.getUserCardTemp(token);
        if (data === undefined) {
            return undefined;
        }
        let userState = parseToUserState(data);
        dispatch({
            type: UserActionTypes.UPDATE,
            payload: userState,
        });
        dispatch(updateSessionContact(userState));
        return data;
    }

    const buyVoucher = async (productId: string) => {
        let token = UserTokenContext.getToken();
        if (!token) return undefined;
        let data = await api.buyVoucher(productId, "GoCRM", token);
        return data;
    }


    const changePassword = async (email?: string, code?: string, password?: string, passwordConfirmation?: string) => {
        await api.changePassword(email, code, password, passwordConfirmation);
    }

    const sendActivation = async (email?: string) => {
        await api.sendActivationLink(email);
    }

    return {
        sendActivation,
        changePassword,
        deleteUser,
        updateUser,
        login,
        register,
        logout,
        loginToken,
        loginAuthorizationCode,
        activation,
        refresh,
        qrcode,
        syncVouchers,
        buyVoucher,
        tempCode
    };
};
